import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type CartKind = "focus_package" | "intl_package";
export type CartItem = {
  kind: CartKind;
  id: string;
  title: string;
  price_ngn: number; // already converted to NGN for display + checkout
  subtitle?: string;
};

type Ctx = {
  items: CartItem[];
  count: number;
  totalNgn: number;
  has: (kind: CartKind, id: string) => boolean;
  add: (item: CartItem) => void;
  remove: (kind: CartKind, id: string) => void;
  clear: () => void;
};

const STORAGE_KEY = "eph_cart_v1";
const CartContext = createContext<Ctx | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  const has = useCallback(
    (kind: CartKind, id: string) => items.some((i) => i.kind === kind && i.id === id),
    [items],
  );
  const add = useCallback((item: CartItem) => {
    setItems((prev) => (prev.some((i) => i.kind === item.kind && i.id === item.id) ? prev : [...prev, item]));
  }, []);
  const remove = useCallback((kind: CartKind, id: string) => {
    setItems((prev) => prev.filter((i) => !(i.kind === kind && i.id === id)));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<Ctx>(() => ({
    items, count: items.length,
    totalNgn: items.reduce((s, i) => s + Number(i.price_ngn || 0), 0),
    has, add, remove, clear,
  }), [items, has, add, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
